import React, { useMemo, useState } from "react";

import { Box, Typography } from "@mui/material";
import { isEmpty, sum } from "lodash";
import { withStyles } from "tss-react/mui";

import CalBurnedIcon from "@/components/Icons/CalBurnedIcon";
import {
  ClientExerciseCommentFragment,
  ExerciseDetailsFragment,
} from "@/gql/graphql";

import EmptyView from "./EmptyView";
import ExerciseComments from "./ExerciseComments";
import ExerciseTab from "./ExerciseTab";

interface Props {
  data: ExerciseDetailsFragment[];
  comments: ClientExerciseCommentFragment[];
  coachId?: string;
  renderExercise: (
    exercise: ExerciseDetailsFragment,
    activeIndex: number,
    tabIndex: number
  ) => React.ReactNode;
}

const Container = withStyles(Box, {
  root: {
    display: "flex",
    minHeight: "45vh",
    marginInline: "10px",
    marginTop: "20px",
  },
});

const CaloriedBurnedContainer = withStyles(Box, {
  root: {
    alignItems: "center",
    backgroundColor: "white",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    marginBottom: "8px",
    padding: "12px",
  },
});

const CaloriesBurnedIcon = withStyles(CalBurnedIcon, {
  root: {
    height: 56,
    width: 40,
  },
});

const CaloriesBurnedTitle = withStyles(Typography, {
  root: {
    fontSize: 33,
    fontWeight: 600,
  },
});

const CaloriesBurnedSubtitle = withStyles(Typography, {
  root: {
    fontSize: 10,
    fontWeight: 600,
  },
});

const ExerciseDetailsContainer = withStyles(Box, {
  root: {
    backgroundColor: "white",
    flex: 1,
    marginRight: "16px",
    padding: "24px",
  },
});

const ExerciseDetailsTitle = withStyles(Typography, {
  root: {
    fontSize: 20,
    fontWeight: 700,
  },
});

const ExerciseCommentsContainer = withStyles(Box, {
  root: {
    backgroundColor: "white",
    flex: 1,
    padding: "24px",
  },
});

const ExerciseCommentsTitle = withStyles(Typography, {
  root: {
    fontSize: 20,
    fontWeight: 700,
  },
});

const Layout: React.FC<Props> = ({
  data,
  comments,
  coachId,
  renderExercise,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const exercises = useMemo(() => {
    return data.map((exercise, index) => {
      const isLastItem = index === data.length - 1;

      return (
        <Box key={index}>
          {renderExercise(exercise, index, activeTab)}
          {!isLastItem && <Box height="1px" bgcolor="#EEEEEE" />}
        </Box>
      );
    });
  }, [data, activeTab]);

  const handleChangeTab = (value: number) => setActiveTab(value);

  const totalCaloriesBurned = sum(
    data.map((exercise) => exercise.caloriesBurned)
  );

  return (
    <Container>
      <Box flex={1} marginRight={4}>
        <CaloriedBurnedContainer>
          <CaloriesBurnedIcon />
          <CaloriesBurnedTitle>{totalCaloriesBurned}</CaloriesBurnedTitle>
          <CaloriesBurnedSubtitle>KCAL BURNED</CaloriesBurnedSubtitle>
        </CaloriedBurnedContainer>
        {data.map((exercise, index) => (
          <ExerciseTab
            key={index}
            active={activeTab == index}
            name={exercise.exerciseType.name}
            onClick={() => handleChangeTab(index)}
          />
        ))}
      </Box>
      <Box display="flex" flex={5}>
        <ExerciseDetailsContainer>
          <ExerciseDetailsTitle>Exercise details</ExerciseDetailsTitle>
          {isEmpty(exercises) ? <EmptyView /> : exercises}
        </ExerciseDetailsContainer>
        <ExerciseCommentsContainer>
          <ExerciseCommentsTitle>Comments</ExerciseCommentsTitle>
          <ExerciseComments coachId={coachId} comments={comments} />
        </ExerciseCommentsContainer>
      </Box>
    </Container>
  );
};

export default Layout;
